import { useState, type ReactNode } from "react";

import {
  desktopUpdateButtonLabel,
  desktopUpdateConfirmLabel,
  useDesktopUpdates,
} from "../desktop-updates";
import ConfirmDialog from "./ConfirmDialog";

export function DesktopUpdateButton() {
  const { desktop, enrolled, offer, install, viewer, service } =
    useDesktopUpdates();
  const [confirmOpen, setConfirmOpen] = useState(false);

  if (!desktop || !enrolled || !offer.available) {
    return null;
  }

  const error = install.error ?? viewer.error ?? service.error;
  const description = [
    offer.releaseNotes,
    offer.viewerAvailable
      ? "The viewer downloads the signed package, verifies it, and restarts."
      : null,
    offer.serviceAvailable && !offer.viewerAvailable
      ? "The background sync service replaces its signed binary without stopping the replica database."
      : null,
  ]
    .filter((value): value is string => value !== null && value.length > 0)
    .join(" ");

  return (
    <>
      <button
        className="header-icon-button header-icon-button--update"
        type="button"
        aria-label={desktopUpdateButtonLabel(offer)}
        title={desktopUpdateButtonLabel(offer)}
        disabled={install.isPending}
        onClick={() => {
          setConfirmOpen(true);
        }}
      >
        <UpdateGlyph />
        <span className="header-icon-button__badge" aria-hidden="true" />
      </button>
      <ConfirmDialog
        open={confirmOpen}
        label="Desktop update"
        title={desktopUpdateButtonLabel(offer)}
        description={description}
        confirmLabel={desktopUpdateConfirmLabel(offer, install.isPending)}
        cancelLabel="Later"
        confirmTone="primary"
        onConfirm={() => {
          install.mutate(undefined, {
            onSettled: () => {
              setConfirmOpen(false);
            },
          });
        }}
        onCancel={() => {
          setConfirmOpen(false);
        }}
      />
      {error === null || confirmOpen ? null : (
        <p className="visually-hidden" role="alert">
          {error.message.replaceAll("_", " ")}
        </p>
      )}
    </>
  );
}

function UpdateGlyph(): ReactNode {
  return (
    <svg
      className="header-icon-button__glyph"
      viewBox="0 0 20 20"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="square"
      strokeLinejoin="miter"
    >
      <path d="M10 3.5v8.5" />
      <path d="M6.5 8 10 4.5 13.5 8" />
      <path d="M4.5 13.5v2h11v-2" />
    </svg>
  );
}
